import { inr, fmtDate } from "../lib/format";
import { Badge } from "./ui/badge";

const STATUS_STYLE = {
  "Capital Locked": "border-amber-500/25 bg-amber-500/10 text-amber-300",
  "Partially Locked": "border-yellow-500/25 bg-yellow-500/10 text-yellow-300",
  "Capital Withdrawable": "border-emerald-500/25 bg-emerald-500/10 text-emerald-300",
  "Interest Withdrawable": "border-emerald-500/25 bg-emerald-500/10 text-emerald-300",
  "Interest Locked": "border-white/10 bg-white/[0.03] text-zinc-400",
  "Redemption Eligible": "border-yellow-500/25 bg-yellow-500/10 text-yellow-300",
  "Redemption Locked": "border-white/10 bg-white/[0.03] text-zinc-400",
};

const TRANCHE_STYLE = {
  Locked: "border-amber-500/25 bg-amber-500/10 text-amber-300",
  Eligible: "border-emerald-500/25 bg-emerald-500/10 text-emerald-300",
  "Fully Withdrawn": "border-white/10 bg-white/[0.03] text-zinc-500",
};

const Item = ({ label, value, testId, sub }) => (
  <div>
    <p className="label-caps">{label}</p>
    <p className="tnum text-sm font-semibold text-white mt-1.5" data-testid={testId}>{value}</p>
    {sub && <p className="text-[10px] text-amber-300 mt-1 tnum">{sub}</p>}
  </div>
);

export default function WithdrawalPanel({ w, testId = "withdrawal-panel" }) {
  if (!w || !w.available) return null;
  const tranches = w.tranches || [];
  return (
    <div className="glass-card p-6" data-testid={testId}>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
        <h3 className="font-heading font-semibold text-white">Withdrawal Eligibility</h3>
        <div className="flex flex-wrap gap-2">
          {(w.statuses || []).map((s) => (
            <Badge key={s} variant="outline"
              className={`text-[10px] ${STATUS_STYLE[s] || "border-white/10 text-zinc-400"}`}
              data-testid={`${testId}-status-${s.toLowerCase().replace(/\s+/g, "-")}`}>{s}</Badge>
          ))}
        </div>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-5">
        <Item label="AUM Onboarded Date" value={fmtDate(w.aum_onboarded_date)} testId={`${testId}-onboarded`} />
        <Item label="Withdrawal Option" value={w.withdrawal_option} testId={`${testId}-option`} />
        <Item label="Next Eligible Withdrawal" testId={`${testId}-next`}
          value={w.withdrawal_option === "At Redemption" ? "At Redemption" : fmtDate(w.next_interest_date)} />
        <Item label="Capital Unlock Date" value={fmtDate(w.capital_unlock_date)} testId={`${testId}-unlock`}
          sub={w.capital_locked ? `Locked — ${w.lock_days_remaining} days remaining` : null} />
        {w.original_capital != null && (
          <Item label="Original Capital" value={inr(w.original_capital)} testId={`${testId}-original-capital`} />
        )}
        {w.additional_capital != null && (
          <Item label="Additional Purchase Capital" value={inr(w.additional_capital)} testId={`${testId}-additional-capital`} />
        )}
        {w.locked_capital != null && (
          <Item label="Currently Locked Capital" value={inr(w.locked_capital)} testId={`${testId}-locked-capital`} />
        )}
        {w.eligible_capital != null && (
          <Item label="Redemption-Eligible Capital" value={inr(w.eligible_capital)} testId={`${testId}-eligible-capital`} />
        )}
        <Item label="Interest / Profit Earned" value={inr(w.earned_profit)} testId={`${testId}-earned`} />
        <Item label="Currently Withdrawable" value={inr(w.withdrawable_profit)} testId={`${testId}-withdrawable`} />
        <Item label="Non-Withdrawable Profit" value={inr(w.non_withdrawable_profit)} testId={`${testId}-non-withdrawable`} />
        <Item label="Total Capital" value={inr(w.capital)} testId={`${testId}-capital`} />
        <Item label="Total Portfolio Value" value={inr(w.current_value)} testId={`${testId}-value`} />
        <Item label="Exit Load (configured)" value={`${w.exit_load_pct}%`} testId={`${testId}-exit-load`} />
        <Item label="Profit Sharing (configured)" value={`${w.profit_sharing_pct}%`} testId={`${testId}-profit-sharing`} />
      </div>

      {tranches.length > 0 && (
        <div className="mt-6 pt-5 border-t border-white/[0.06]" data-testid={`${testId}-tranches`}>
          <p className="label-caps mb-3">Capital Deployments — per-tranche 90-day lock</p>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-white/[0.06]">
                  {["Deployment", "Date", "Amount", "Withdrawn", "Remaining", "Lock End", "Status"].map((h) => (
                    <th key={h} className="px-3 py-2 font-semibold text-left text-[10px] uppercase tracking-[0.14em] text-zinc-500">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {tranches.map((t) => (
                  <tr key={t.transaction_id} className="border-b border-white/[0.04] last:border-0"
                    data-testid={`${testId}-tranche-${t.transaction_id}`}>
                    <td className="px-3 py-2.5">
                      <p className="text-xs font-semibold text-white">{t.kind}</p>
                      <p className="text-[10px] text-zinc-600 tnum">{t.reference_number || ""}</p>
                    </td>
                    <td className="px-3 py-2.5 text-xs tnum text-zinc-400 whitespace-nowrap">{fmtDate(t.date)}</td>
                    <td className="px-3 py-2.5 text-xs tnum text-zinc-200">{inr(t.amount)}</td>
                    <td className="px-3 py-2.5 text-xs tnum text-zinc-500">{inr(t.withdrawn)}</td>
                    <td className="px-3 py-2.5 text-xs tnum text-white">{inr(t.remaining)}</td>
                    <td className="px-3 py-2.5 text-xs tnum text-zinc-400 whitespace-nowrap">{fmtDate(t.lock_end_date)}</td>
                    <td className="px-3 py-2.5">
                      <Badge variant="outline" className={`text-[10px] ${TRANCHE_STYLE[t.status] || "border-white/10 text-zinc-400"}`}
                        data-testid={`${testId}-tranche-status-${t.transaction_id}`}>{t.status}</Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
